// Enhanced Subscription Service for Supabase + Render Backend
import { shouldUseDryRun } from "../utils/environment";
import { shouldToastApiError } from '@/app/_module/utils/apiResponseHandler';
import { showToast } from "../utils/show-toast";
import { transformErrorResponse } from "../utils/transform-error-response";

import api from "./api";
import { getApiUrl } from "./config";
import { endpoint } from "./endpoints";
import { handleApiCall, normalizeApiError } from "../utils/apiResponseHandler";

// Enhanced Subscription Types
export interface SubscriptionPlan {
  id: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  interval: 'month' | 'year' | 'week' | 'day';
  intervalCount: number;
  trialPeriodDays?: number;
  features: string[];
  isActive: boolean;
  isPopular?: boolean;
  stripePriceId?: string;
  stripeProductId?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface Subscription {
  id: string;
  userId: string;
  planId: string;
  status: 'active' | 'inactive' | 'trialing' | 'past_due' | 'canceled' | 'expired' | 'unpaid' | 'incomplete';
  startDate: string;
  endDate?: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialStart?: string;
  trialEnd?: string;
  canceledAt?: string;
  cancelAtPeriodEnd: boolean;
  autoRenew: boolean;
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
  stripeStatus?: string;
  lastBilledAt?: string;
  nextBillingAt?: string;
  plan: SubscriptionPlan;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCheckoutSessionRequest {
  planId: string;
  successUrl?: string;
  cancelUrl?: string;
  allowPromotionCodes?: boolean;
  trialPeriodDays?: number;
  metadata?: Record<string, unknown>;
}

export interface CheckoutSession {
  id: string;
  url: string;
  transactionId: string;
  planId: string;
  expiresAt: string;
  status: 'open' | 'complete' | 'expired';
  metadata?: Record<string, unknown>;
}

export interface SubscriptionPlansResponse {
  data: SubscriptionPlan[];
  total: number;
  page: number;
  totalPages: number;
  meta?: {
    page: number;
    totalItems: number;
    totalPages: number;
    itemCount: number;
    nextPage?: number;
    prevPage?: number;
    offset: number;
  };
}

export interface CheckoutSessionResponse {
  statusCode: number;
  message: string;
  data: {
    checkoutUrl: string;
    sessionId: string;
    transactionId: string;
  };
  timestamp: string;
  timeTakenMs: number;
}

export interface SubscriptionResponse {
  statusCode: number;
  message: string;
  data: Subscription;
  timestamp: string;
  timeTakenMs: number;
}

export interface SubscriptionStatsResponse {
  totalSubscriptions: number;
  activeSubscriptions: number;
  trialSubscriptions: number;
  canceledSubscriptions: number;
  totalRevenue: number;
  monthlyRecurringRevenue: number;
  averageRevenuePerUser: number;
  churnRate: number;
  byPlan: Record<string, number>;
  byStatus: Record<string, number>;
}

// Enhanced Subscription Service Class
class EnhancedSubscriptionService {
  // Validate subscription data
  private validateSubscriptionData(data: unknown, operation: string) {
    const obj = (data && typeof data === 'object') ? (data as Record<string, unknown>) : {};
    const planId = typeof obj.planId === 'string' ? obj.planId : undefined;
    const successUrl = typeof obj.successUrl === 'string' ? obj.successUrl : undefined;
    const cancelUrl = typeof obj.cancelUrl === 'string' ? obj.cancelUrl : undefined;
    const trialPeriodDays = typeof obj.trialPeriodDays === 'number' ? obj.trialPeriodDays : undefined;

    if (operation === 'checkout') {
      if (!planId?.trim()) {
        throw new Error('Plan ID is required for checkout');
      }
    }
    
    // Validate URLs if provided
    if (successUrl && !this.isValidUrl(successUrl)) {
      throw new Error('Invalid success URL format');
    }
    if (cancelUrl && !this.isValidUrl(cancelUrl)) {
      throw new Error('Invalid cancel URL format');
    }
    
    // Validate trial period
    if (typeof trialPeriodDays === 'number' && (trialPeriodDays < 0 || trialPeriodDays > 365)) {
      throw new Error('Trial period must be between 0 and 365 days');
    }
  }

  private isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  // Handle subscription errors
  private handleError(error: unknown, operation: string): never {

    const normalized = normalizeApiError(error);
    const message = normalized.message || `Failed to ${operation}`;
    const retrySuffix = typeof normalized.retryAfterSeconds === 'number'
      ? ` (retry after ${normalized.retryAfterSeconds}s)`
      : '';
    // silent = MFA-window 403 the interceptor self-recovers — never toast it
    if (shouldToastApiError(normalized, operation)) { showToast(`${message}${retrySuffix}`, "error") }

    const e = new Error(message) as Error & { code?: string | number; retryAfterSeconds?: number };
    e.code = normalized.code;
    e.retryAfterSeconds = normalized.retryAfterSeconds;
    throw e;
  }

  // Get subscription plans with filtering and pagination
  async getSubscriptionPlans(params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
    interval?: string;
    sortBy?: 'name' | 'price' | 'createdAt';
    sortOrder?: 'asc' | 'desc';
  }): Promise<SubscriptionPlansResponse> {
    try {
      // Validate parameters
      if (params?.page && params.page < 1) {
        throw new Error('Page number must be greater than 0');
      }
      if (params?.limit && (params.limit < 1 || params.limit > 1000)) {
        throw new Error('Limit must be between 1 and 1000');
      }

      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set("page", params.page.toString());
      if (params?.limit) searchParams.set("limit", params.limit.toString());
      if (params?.search) searchParams.set("search", params.search);
      if (params?.isActive !== undefined) searchParams.set("isActive", params.isActive.toString());
      if (params?.interval) searchParams.set("interval", params.interval);
      if (params?.sortBy) searchParams.set("sortBy", params.sortBy);
      if (params?.sortOrder) searchParams.set("sortOrder", params.sortOrder);

      const url = `${getApiUrl(endpoint.subscriptionPlans.getSubscriptionPlans)}?${searchParams.toString()}`;
      const result = await handleApiCall<{
        items: SubscriptionPlan[];
        meta: {
          page: number;
          totalItems: number;
          totalPages: number;
          itemCount: number;
          nextPage?: number;
          prevPage?: number;
          offset: number;
        };
      }>(async () => {
        const response = await api.get<{
          statusCode: number;
          message: string;
          data: {
            items: SubscriptionPlan[];
            meta: {
              page: number;
              totalItems: number;
              totalPages: number;
              itemCount: number;
              nextPage?: number;
              prevPage?: number;
              offset: number;
            };
          };
          timestamp: string;
          timeTakenMs: number;
        }>({ url });
        return response;
      });

      // Handle different response structures from backend
      if (result?.items && Array.isArray(result.items)) {
        const { items, meta } = result;
        
        // Validate and sanitize plan data
        const sanitizedItems = items.map(plan => ({
          ...plan,
          name: plan.name || 'Untitled Plan',
          description: plan.description || '',
          features: plan.features || [],
          isActive: Boolean(plan.isActive),
          price: Number(plan.price) || 0,
          currency: plan.currency || 'USD',
        }));
        
        return {
          data: sanitizedItems,
          total: meta?.totalItems || sanitizedItems.length,
          page: meta?.page || 1,
          totalPages: meta?.totalPages || 1,
          meta,
        };
      }

      throw new Error("Invalid response structure received from server");
    } catch (error) {
      this.handleError(error, 'fetch subscription plans');
    }
  }

  // Get subscription plan by ID
  async getSubscriptionPlanById(planId: string): Promise<SubscriptionPlan> {
    try {
      // Validate plan ID
      if (!planId || planId.trim() === '') {
        throw new Error('Plan ID is required');
      }

      const response = await api.get<{
        statusCode: number;
        message: string;
        data: SubscriptionPlan;
        timestamp: string;
        timeTakenMs: number;
      }>({
        url: getApiUrl(endpoint.subscriptionPlans.getSubscriptionPlanById(planId)),
      });

      // Validate response structure
      if (!response.data?.data) {
        throw new Error('Invalid response format from subscription plan API');
      }

      // Ensure plan data has required fields
      const plan = {
        ...response.data.data,
        name: response.data.data.name || 'Untitled Plan',
        description: response.data.data.description || '',
        features: response.data.data.features || [],
        isActive: Boolean(response.data.data.isActive),
        price: Number(response.data.data.price) || 0,
        currency: response.data.data.currency || 'USD',
      };

      return plan;
    } catch (error) {
      this.handleError(error, `fetch subscription plan ${planId}`);
    }
  }

  // Create checkout session
  async createCheckoutSession(checkoutData: CreateCheckoutSessionRequest): Promise<CheckoutSession> {
    try {
      // Validate checkout data
      this.validateSubscriptionData(checkoutData, 'checkout');

      // Use environment-based dry run unless explicitly overridden
      const dryRun = shouldUseDryRun();
      
      // Set default URLs if not provided
      const baseUrl = typeof window !== 'undefined' ? window.location.origin : (process.env.NEXT_PUBLIC_FRONTEND_URL || 'https://insuracentral.com');
      const defaultSuccessUrl = `${baseUrl}/settings/billing/success`;
      const defaultCancelUrl = `${baseUrl}/settings/billing/cancel`;
      
      // Sanitize and prepare data for backend
      const sanitizedData = {
        planId: checkoutData.planId.trim(),
        successUrl: checkoutData.successUrl?.trim() || defaultSuccessUrl,
        cancelUrl: checkoutData.cancelUrl?.trim() || defaultCancelUrl,
        allowPromotionCodes: checkoutData.allowPromotionCodes ?? true,
        trialPeriodDays: checkoutData.trialPeriodDays,
        metadata: checkoutData.metadata || {},
        dryRun,
      };

      const response = await api.post<CheckoutSessionResponse>({
        url: getApiUrl(endpoint.subscriptions.checkoutSession),
        body: sanitizedData,
      });

      // Validate response
      if (!response.data?.data) {
        throw new Error('Invalid response format from checkout session API');
      }

      const checkoutSession: CheckoutSession = {
        id: response.data.data.sessionId,
        url: response.data.data.checkoutUrl,
        transactionId: response.data.data.transactionId,
        planId: checkoutData.planId,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours from now
        status: 'open',
        metadata: checkoutData.metadata,
      };

      showToast("Checkout session created successfully", "success");
      return checkoutSession;
    } catch (error) {
      this.handleError(error, 'create checkout session');
    }
  }

  // Complete dry run subscription (for testing)
  async completeDryRunSubscription(transactionId: string): Promise<{ success: boolean; message: string }> {
    try {
      // Only allow dry run completion in non-production environments
      if (process.env.NODE_ENV === 'production') {
        throw new Error('Dry run subscription completion not allowed in production');
      }

      // Validate transaction ID
      if (!transactionId || transactionId.trim() === '') {
        throw new Error('Transaction ID is required for dry run completion');
      }

      const response = await api.post<{
        statusCode: number;
        message: string;
        data: {
          success: boolean;
          message: string;
          transactionId: string;
          dryRun: boolean;
        };
        timestamp: string;
        timeTakenMs: number;
      }>({
        url: getApiUrl(endpoint.subscriptions.completeDryRunSubscription),
        body: { transactionId: transactionId.trim() },
      });

      // Validate response - check for standardized format first
      if (response.data?.statusCode !== undefined) {
        if (response.data.statusCode === 200 || response.data.statusCode === 201) {
          showToast("Dry run subscription completed successfully", "success");
          
          return {
            success: response.data.data.success,
            message: response.data.data.message,
          };
        } else {
          throw new Error(response.data.message || 'Dry run subscription completion failed');
        }
      }
      
      // Handle legacy format (backward compatibility)
      if (response.data?.data) {
        if (response.data.data.success) {
          showToast("Dry run subscription completed successfully", "success");
        }

        return {
          success: response.data.data.success,
          message: response.data.data.message,
        };
      }

      throw new Error('Invalid response format from dry run completion API');
    } catch (error) {
      this.handleError(error, 'complete dry run subscription');
    }
  }

  // Get user's active subscription
  async getMyActiveSubscription(): Promise<Subscription | null> {
    try {
      const response = await api.get<{
        statusCode: number;
        message: string;
        data: Subscription | null;
        timestamp: string;
        timeTakenMs: number;
      }>({
        url: getApiUrl(endpoint.subscriptions.getMyActiveSubscription),
      });

      // Handle case where user has no active subscription
      if (!response.data?.data) {
        return null;
      }

      // Ensure subscription data has required fields
      const subscription = {
        ...response.data.data,
        cancelAtPeriodEnd: Boolean(response.data.data.cancelAtPeriodEnd),
        autoRenew: Boolean(response.data.data.autoRenew),
        plan: {
          ...response.data.data.plan,
          name: response.data.data.plan.name || 'Unknown Plan',
          features: response.data.data.plan.features || [],
          isActive: Boolean(response.data.data.plan.isActive),
          price: Number(response.data.data.plan.price) || 0,
        },
      };

      return subscription;
    } catch (error) {
      // Don't show error toast for 404 (no active subscription)
      if (error && typeof error === 'object' && 'response' in (error as object)) {
        const resp = (error as { response?: { status?: unknown } }).response;
        const status = typeof resp?.status === 'number' ? resp.status : undefined;
        if (status === 404) {
          return null;
        }
      }
      this.handleError(error, 'fetch active subscription');
    }
  }

  // Get all user subscriptions
  async getMySubscriptions(params?: {
    page?: number;
    limit?: number;
    status?: string;
    sortBy?: 'createdAt' | 'startDate' | 'endDate';
    sortOrder?: 'asc' | 'desc';
  }): Promise<{
    data: Subscription[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    try {
      // Validate parameters
      if (params?.page && params.page < 1) {
        throw new Error('Page number must be greater than 0');
      }
      if (params?.limit && (params.limit < 1 || params.limit > 1000)) {
        throw new Error('Limit must be between 1 and 1000');
      }

      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set("page", params.page.toString());
      if (params?.limit) searchParams.set("limit", params.limit.toString());
      if (params?.status) searchParams.set("status", params.status);
      if (params?.sortBy) searchParams.set("sortBy", params.sortBy);
      if (params?.sortOrder) searchParams.set("sortOrder", params.sortOrder);

      const url = `${getApiUrl(endpoint.subscriptions.getMySubscriptions)}?${searchParams.toString()}`;
      const response = await api.get<{
        statusCode: number;
        message: string;
        data: {
          items: Subscription[];
          meta: {
            page: number;
            totalItems: number;
            totalPages: number;
            itemCount: number;
            nextPage?: number;
            prevPage?: number;
            offset: number;
          };
        };
        timestamp: string;
        timeTakenMs: number;
      }>({ url });

      // Handle different response structures from backend
      if (response.data?.data?.items && Array.isArray(response.data.data.items)) {
        const { items, meta } = response.data.data;
        
        // Validate and sanitize subscription data
        const sanitizedItems = items.map(subscription => ({
          ...subscription,
          cancelAtPeriodEnd: Boolean(subscription.cancelAtPeriodEnd),
          autoRenew: Boolean(subscription.autoRenew),
          plan: {
            ...subscription.plan,
            name: subscription.plan.name || 'Unknown Plan',
            features: subscription.plan.features || [],
            isActive: Boolean(subscription.plan.isActive),
            price: Number(subscription.plan.price) || 0,
          },
        }));
        
        return {
          data: sanitizedItems,
          total: meta?.totalItems || sanitizedItems.length,
          page: meta?.page || 1,
          totalPages: meta?.totalPages || 1,
        };
      }

      return {
        data: [],
        total: 0,
        page: 1,
        totalPages: 1,
      };
    } catch (error) {
      this.handleError(error, 'fetch user subscriptions');
    }
  }

  // Cancel subscription
  async cancelSubscription(subscriptionId: string, cancelAtPeriodEnd = true): Promise<Subscription> {
    try {
      // Validate subscription ID
      if (!subscriptionId || subscriptionId.trim() === '') {
        throw new Error('Subscription ID is required for cancellation');
      }

      const response = await api.post<SubscriptionResponse>({
        url: getApiUrl(endpoint.subscriptions.cancelSubscription(subscriptionId)),
        body: { cancelAtPeriodEnd },
      });

      // Validate response
      if (!response.data?.data) {
        throw new Error('Invalid response format from cancel subscription API');
      }

      const message = cancelAtPeriodEnd 
        ? "Subscription will be canceled at the end of the current period"
        : "Subscription canceled immediately";
      
      showToast(message, "success");
      return response.data.data;
    } catch (error) {
      this.handleError(error, `cancel subscription ${subscriptionId}`);
    }
  }

  // Reactivate subscription
  async reactivateSubscription(subscriptionId: string): Promise<Subscription> {
    try {
      // Validate subscription ID
      if (!subscriptionId || subscriptionId.trim() === '') {
        throw new Error('Subscription ID is required for reactivation');
      }

      const response = await api.post<SubscriptionResponse>({
        url: getApiUrl(endpoint.subscriptions.reactivateSubscription(subscriptionId)),
        body: {},
      });

      // Validate response
      if (!response.data?.data) {
        throw new Error('Invalid response format from reactivate subscription API');
      }

      showToast("Subscription reactivated successfully", "success");
      return response.data.data;
    } catch (error) {
      this.handleError(error, `reactivate subscription ${subscriptionId}`);
    }
  }

  // Update subscription
  async updateSubscription(subscriptionId: string, updateData: {
    planId?: string;
    autoRenew?: boolean;
    metadata?: Record<string, unknown>;
  }): Promise<Subscription> {
    try {
      // Validate subscription ID
      if (!subscriptionId || subscriptionId.trim() === '') {
        throw new Error('Subscription ID is required for update');
      }

      // Validate update data
      if (updateData.planId && !updateData.planId.trim()) {
        throw new Error('Plan ID cannot be empty');
      }

      const sanitizedData = {
        planId: updateData.planId?.trim(),
        autoRenew: updateData.autoRenew,
        metadata: updateData.metadata || {},
      };

      const response = await api.put({
        url: getApiUrl(endpoint.subscriptions.updateSubscription(subscriptionId)),
        body: sanitizedData,
      }) as { data: SubscriptionResponse };

      // Validate response
      if (!response.data?.data) {
        throw new Error('Invalid response format from update subscription API');
      }

      showToast("Subscription updated successfully", "success");
      return response.data.data;
    } catch (error) {
      this.handleError(error, `update subscription ${subscriptionId}`);
    }
  }

  // Get subscription statistics (admin only)
  async getSubscriptionStats(): Promise<SubscriptionStatsResponse> {
    try {
      const response = await api.get<{
        statusCode: number;
        message: string;
        data: SubscriptionStatsResponse;
        timestamp: string;
        timeTakenMs: number;
      }>({
        url: getApiUrl(endpoint.subscriptions.getSubscriptionStats),
      });

      if (!response.data?.data) {
        throw new Error('Invalid response format from subscription stats API');
      }

      return response.data.data;
    } catch (error) {
      this.handleError(error, 'fetch subscription statistics');
    }
  }

  // Helper method to format currency
  formatCurrency(amount: number, currency = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100); // Assuming amounts are in cents
  }

  // Helper method to format interval
  formatInterval(interval: string, intervalCount = 1): string {
    const intervalMap: Record<string, string> = {
      day: 'day',
      week: 'week',
      month: 'month',
      year: 'year',
    };

    const intervalName = intervalMap[interval] || interval;
    return intervalCount === 1 ? intervalName : `${intervalCount} ${intervalName}s`;
  }

  // Helper method to get subscription status color
  getStatusColor(status: string): string {
    const statusColors: Record<string, string> = {
      active: 'green',
      trialing: 'blue',
      past_due: 'yellow',
      canceled: 'red',
      expired: 'gray',
      unpaid: 'red',
      incomplete: 'orange',
      inactive: 'gray',
    };

    return statusColors[status] || 'gray';
  }
}

// Export singleton instance
export const subscriptionService = new EnhancedSubscriptionService();
export default subscriptionService;